import type { RouteRecordRaw } from 'vue-router'

import { DataViewType } from '@/constants/relations'
import { routeOutboxDetail } from '@/routers/routes/mainRoutes/routeActivities/communicationRoutes'
import { ensureRootRoutePath, getRouteName, type RedirectRoute } from '@/utilities/routers'

const routeRedirectName = '/redirect'
const routeRedirectPath = ensureRootRoutePath(routeRedirectName)

// Function to parse routeRequest from query parameters
const parseRouteRequest = (
  routeRequest: unknown,
): null | {
  isOutbox?: boolean
  routeOptions: unknown
  target: string
  targetDoctype: string
  targetViewtype: string
} => {
  let parsedRouteRequest: null | {
    isOutbox?: boolean
    routeOptions: unknown
    target: string
    targetDoctype: string
    targetViewtype: string
  } = null

  if (typeof routeRequest === 'string') {
    try {
      const obj = JSON.parse(routeRequest)
      if (
        obj &&
        typeof obj.target === 'string' &&
        typeof obj.targetDoctype === 'string' &&
        typeof obj.targetViewtype === 'string' &&
        'routeOptions' in obj
      ) {
        parsedRouteRequest = {
          isOutbox: obj.isOutbox === true,
          routeOptions: obj.routeOptions,
          target: obj.target,
          targetDoctype: obj.targetDoctype,
          targetViewtype: obj.targetViewtype,
        }
      }
    } catch (exception) {
      console.error('Failed to parse routeRequest:', exception)
    }
  }

  if (!parsedRouteRequest) {
    return null
  }

  return parsedRouteRequest
}

const routeRedirect: RouteRecordRaw = {
  path: routeRedirectPath,
  redirect: (to) => {
    // Extract routeRequest from query parameters
    const { routeRequest } = to.query

    // Default redirect route in case of errors
    const redirectRoute: RedirectRoute = {
      name: 'error',
      params: {},
      query: {},
    }

    // Parse the routeRequest
    const parsedRouteRequest = parseRouteRequest(routeRequest)

    // Handle invalid routeRequest
    if (!parsedRouteRequest) {
      console.error('Invalid routeRequest:', routeRequest)
      return redirectRoute
    }

    const { isOutbox, routeOptions, target, targetDoctype, targetViewtype } = parsedRouteRequest

    // Get the route name based on targetDoctype and targetViewtype
    const isOutboxDetail =
      isOutbox &&
      targetViewtype === 'Detail' &&
      targetDoctype.toLowerCase().replace(/ /gu, '-') === 'communication'
    const routeName = isOutboxDetail
      ? routeOutboxDetail.name
      : getRouteName(targetDoctype, targetViewtype)

    // Handle case where no route name is found
    if (!routeName) {
      console.error('No route found for doctype:', targetDoctype, 'and viewtype:', targetViewtype)
      return redirectRoute
    }

    // Set the redirect route name and parameters
    redirectRoute.name = routeName
    redirectRoute.params = { id: decodeURIComponent(target) }

    const query: Record<string, string> = {}

    if (targetViewtype === 'Kanban') {
      query['selectedViewType'] = DataViewType.kanban
    }

    // Append routeOptions to query if they exist
    if (
      routeOptions !== null &&
      routeOptions !== undefined &&
      Object.keys(routeOptions).length > 0
    ) {
      query['routeOptions'] = JSON.stringify(routeOptions)
    }

    if (Object.keys(query).length > 0) {
      redirectRoute.query = query
    }

    return redirectRoute
  },
}

export { routeRedirect }
